//Componentes generales
import React, { useState, useMemo, useContext, useRef, useEffect } from "react";
import { VariableSizeList } from "react-window";
import SearchInput from "../../components/searchInput/SearchInput";
import RenderRow from "../renderRows/RRProducts";
import windowSizeContext from "../../context/windowSize/windowSizeContext";
import {
  esAtributoMarca,
  CODATR_TIPO,
  CODATR_GRUPO,
  CODATR_SUBGRUPO,
  cumpleFiltroAtributo,
} from "../../utils/atributosProducto";
import normalizeText from "../../utils/normalizeText";

//Estilos
import "./ProductList.css";

const BASE_ITEM_HEIGHT = 140;
const ATTR_HEADER_HEIGHT = 46;
const ATTR_ROW_HEIGHT = 36;
// Espacio del contenedor de la tabla desplegable (padding + margin de .atributosProductoListDiv).
// Se sobreestima a propósito: en una lista virtualizada es preferible dejar un pequeño espacio de
// más que quedarse corto y que la fila siguiente se superponga con la tabla de atributos.
const ATTR_TABLE_MARGIN = 48;
const ATTR_EMPTY_HEIGHT = 56;

const productKey = (p) => `${p.tipPro}-${p.artCod}`;

/*
    Componente encargado de la vista de los productos disponibles.
    Donde productos es la lista de productos disponibles recuperados del context (ya con sus
    atributos anidados).
*/
function ProductList({ productos = [], filtroTipo, filtroGrupo, filtroSubgrupo, actualizarProductDBIndividual, ocultarPrecio }) {

  //state de filtro de texto libre.
  const [filter, setFilter] = useState("");

  //state de las filas con la tabla de atributos desplegada.
  const [expandedKeys, setExpandedKeys] = useState(new Set());

  const listRef = useRef(null);

  // Calcula el valor del height según el tamaño de la pantalla y el tamaño del navbar
  const wSContext = useContext(windowSizeContext);
  let { height } = wSContext;

  /*
        Manejador ante cambios en filtro.
        Donde data es el texto ingresado por el usuario.
    */
  const handdlerSearchInput = (data) => {
    setFilter(data);
  };

  const handleToggleExpand = (index) => {
    const producto = filteredProductos[index];
    if (!producto) return;
    const key = productKey(producto);
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const filteredProductos = useMemo(() => {
    const f = normalizeText(filter.trim());
    return productos.filter((p) => {
      const matchesText =
        f === "" ||
        normalizeText((p.descrp || "").trim()).includes(f) ||
        normalizeText((p.codRed || "").trim()).includes(f) ||
        normalizeText((p.tipPro || "").trim()).includes(f) ||
        normalizeText((p.artCod || "").trim()).includes(f) ||
        (p.atributos || []).some(
          (a) =>
            a.debaja !== "S" &&
            (normalizeText((a.codAtr || "").trim()).includes(f) ||
              normalizeText((a.descrpAtr || "").trim()).includes(f) ||
              normalizeText((a.valAtr || "").trim()).includes(f) ||
              normalizeText((a.descrpVal || "").trim()).includes(f))
        );

      return (
        matchesText &&
        cumpleFiltroAtributo(p.atributos, CODATR_TIPO, filtroTipo) &&
        cumpleFiltroAtributo(p.atributos, CODATR_GRUPO, filtroGrupo) &&
        cumpleFiltroAtributo(p.atributos, CODATR_SUBGRUPO, filtroSubgrupo)
      );
    });
  }, [productos, filter, filtroTipo, filtroGrupo, filtroSubgrupo]);

  //Cada vez que cambia la lista filtrada o qué filas están desplegadas, hay que invalidar las alturas ya calculadas.
  useEffect(() => {
    if (listRef.current) listRef.current.resetAfterIndex(0);
  }, [filteredProductos, expandedKeys]);

  const getItemSize = (index) => {
    const producto = filteredProductos[index];
    if (!producto) return BASE_ITEM_HEIGHT;
    if (!expandedKeys.has(productKey(producto))) return BASE_ITEM_HEIGHT;

    const atributosVisibles = (producto.atributos || []).filter((a) => !esAtributoMarca(a.codAtr) && a.debaja !== "S");
    const extra =
      atributosVisibles.length > 0
        ? ATTR_HEADER_HEIGHT + atributosVisibles.length * ATTR_ROW_HEIGHT + ATTR_TABLE_MARGIN
        : ATTR_EMPTY_HEIGHT + ATTR_TABLE_MARGIN;

    return BASE_ITEM_HEIGHT + extra;
  };

  return (
    <div>
      <div className="product-search-wrapper" style={{ marginLeft: "-150px" }}>
        <SearchInput
          defaultValue={filter}
          buscar={handdlerSearchInput}
        />
      </div>
      <div className="container-product-list">
        {filteredProductos.length === 0 ? (
          <p className="no-records-message">No hay registros para mostrar</p>
        ) : (
          <VariableSizeList
            ref={listRef}
            height={height - 315}
            itemCount={filteredProductos.length}
            itemKey={(index) => productKey(filteredProductos[index]) || index}
            itemSize={getItemSize}
            width={"100%"}
          >
            {({ index, style }) => (
              <RenderRow
                index={index}
                style={style}
                productos={filteredProductos}
                isExpanded={expandedKeys.has(productKey(filteredProductos[index] || {}))}
                onToggleExpand={handleToggleExpand}
                actualizarProductDBIndividual={actualizarProductDBIndividual}
                ocultarPrecio={ocultarPrecio}
              />
            )}
          </VariableSizeList>
        )}
      </div>
    </div>
  );
}

export default ProductList;
